import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Injectable } from '@angular/core';
import { pick } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  fromEvent,
  map,
  tap,
  Observable,
  auditTime,
  shareReplay,
  switchMap,
  filter,
  startWith,
} from 'rxjs';

/**
 * this value represents widths sum of the bottom components in the Space + a small margin
 * [left toolbar, main toolbar, zoom toolbar]
 */
export const MIN_SPACE_CANVAS_WIDTH_NEEDED = 1202;

/**
 * this value represents the minimum width needed for the bottom toolbar v2 to be enabled
 * not: this should be deperecated once bottom toolbar v2 [mobile] is fully implemented
 */
export const MIN_WIDTH_NEEDED_FOR_BOTTOM_TOOLBAR_V2 = 620;

export const MIN_WIDTH_NEEDED_FOR_LEFT_SECTION_BOTTOM_TOOLBAR_V2 = 800;
export const MIN_WIDTH_NEEDED_FOR_RIGHT_SECTION_BOTTOM_TOOLBAR_V2 = 800;
export const MIN_WIDTH_NEEDED_FOR_MIDDLE_SECTION_BOTTOM_TOOLBAR_V2 = 1080;

export const FIRST_BREAKPOINT_FOR_SCREEN_SHARE_ANNOTATIONS_TOOLBAR = 760;
export const SECOND_BREAKPOINT_FOR_SCREEN_SHARE_ANNOTATIONS_TOOLBAR = 640;

export enum ScreenSize {
  XSMALL = 'xsmall',
  SMALL = 'small',
  XLARGE = 'xlarge',
  MEDIUM = 'medium',
  LARGE = 'large',
}

/**
 * Product layout buckets for chrome (dialog vs sheet, etc.). Ordered so callers can
 * use numeric comparison, e.g. `layoutSize <= LayoutSize.Tablet`.
 *
 * Distinct from {@link ScreenSize}, which maps CDK Material breakpoints.
 */
export enum LayoutSize {
  /** ≤ {@link MOBILE_MAX_WIDTH} (693px). */
  Mobile = 0,
  /** Wider than mobile, ≤ {@link TABLET_MAX_WIDTH} (991.98px). */
  Tablet = 1,
  /** Wider than {@link TABLET_MAX_WIDTH}. */
  Desktop = 2,
}

// mobile breakpoint width, mirrors constants.$mobile-max-width
export const MOBILE_MAX_WIDTH = 693;

// tablet breakpoint width, mirrors constants.$tablet-max-width
// 0.02px is important to follow Bootstrap's breakpoint width calculation.
export const TABLET_MAX_WIDTH = 992 - 0.02;
// bottom sheet container width for panels
export const PANEL_BOTTOM_SHEET_CONTAINER_WIDTH = 768;
// bottom sheet container width for upload panel
export const UPLOAD_PANEL_BOTTOM_SHEET_CONTAINER_WIDTH = 772;
// breakpoint width for panels bottom sheet
export const BOTTOM_SHEET_BREAKPOINT_WIDTH_PANEL = TABLET_MAX_WIDTH;
// breakpoint width for upload panel bottom sheet
export const BOTTOM_SHEET_BREAKPOINT_WIDTH_UPLOAD_PANEL = 1279;
// breakpoint width for wide panels bottom sheet
export const BOTTOM_SHEET_BREAKPOINT_WIDTH_FOR_WIDE_PANELS = 1400;
// breakpoint width for system status panel bottom sheet
export const BOTTOM_SHEET_BREAKPOINT_WIDTH_SYSTEM_STATUS_PANEL = 1100;

@Injectable({
  providedIn: 'root',
})
export class ScreenSizeService {
  private readonly layoutSizeSubject = new BehaviorSubject<LayoutSize>(LayoutSize.Desktop);

  /** Current product layout bucket. Shares state with {@link layoutSize}. */
  readonly layoutSize$ = this.layoutSizeSubject.asObservable();

  get layoutSize(): LayoutSize {
    return this.layoutSizeSubject.value;
  }

  constructor(private breakpointObserver: BreakpointObserver) {
    combineLatest([
      this.observeMaxWidth$(MOBILE_MAX_WIDTH),
      this.observeMaxWidth$(TABLET_MAX_WIDTH),
    ])
      .pipe(
        map(([isMobile, isTabletOrSmaller]) => {
          if (isMobile) {
            return LayoutSize.Mobile;
          }
          if (isTabletOrSmaller) {
            return LayoutSize.Tablet;
          }
          return LayoutSize.Desktop;
        }),
        distinctUntilChanged(),
        tap((layoutSize) => {
          console.log(`[LayoutSize] ${LayoutSize[layoutSize]}`);
        }),
      )
      .subscribe(this.layoutSizeSubject);
  }

  private _mobileMaxWidth = (() => {
    const cssValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--mobile-max-width')
      .trim();
    // Fallback to '693px' if CSS var is missing or empty
    return cssValue || '693px';
  })();

  private readonly breakpointsToObserverSubject = new BehaviorSubject<Set<string>>(
    new Set([
      Breakpoints.XSmall,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge,
      `(max-width: ${this._mobileMaxWidth})`,
      `(min-width: ${MIN_WIDTH_NEEDED_FOR_BOTTOM_TOOLBAR_V2}px)`,
      `(max-width: ${FIRST_BREAKPOINT_FOR_SCREEN_SHARE_ANNOTATIONS_TOOLBAR}px)`,
      `(max-width: ${SECOND_BREAKPOINT_FOR_SCREEN_SHARE_ANNOTATIONS_TOOLBAR}px)`,
    ]),
  );

  private readonly breakpointsToObserver$ = this.breakpointsToObserverSubject
    .asObservable()
    .pipe(map((breakpoints) => Array.from(breakpoints)));

  private genericObserver$ = this.breakpointsToObserver$.pipe(
    switchMap((breakpoints) => this.breakpointObserver.observe(breakpoints)),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  // this is a rxjs operator that takes breakpoints as input and filters from genericObserver$
  filterBreakpointsOperator = (...breakpointsToFilter: string[]) =>
    this.genericObserver$.pipe(
      map((state: BreakpointState) => {
        const breakpoints = pick(state.breakpoints, breakpointsToFilter);

        return {
          breakpoints,
          matches: Object.values(breakpoints).some((value) => value),
        } as BreakpointState;
      }),
    );

  public screenSize$ = this.filterBreakpointsOperator(
    Breakpoints.XSmall,
    Breakpoints.Small,
    Breakpoints.Medium,
    Breakpoints.Large,
    Breakpoints.XLarge,
  ).pipe(
    map(({ breakpoints }) => {
      const truthyBreakPoint = Object.keys(breakpoints).find(
        (breakpoint) => breakpoints[breakpoint],
      );
      switch (truthyBreakPoint) {
        case Breakpoints.XSmall:
          return ScreenSize.XSMALL;
        case Breakpoints.Small:
          return ScreenSize.SMALL;
        case Breakpoints.Medium:
          return ScreenSize.MEDIUM;
        case Breakpoints.Large:
          return ScreenSize.LARGE;
        case Breakpoints.XLarge:
          return ScreenSize.XLARGE;
        default:
          return ScreenSize.LARGE;
      }
    }),
    distinctUntilChanged(),
  );

  private _renderMenuAsBottomSheet = false;
  public renderMenuAsBottomSheet$ = this.screenSize$.pipe(
    map((screenSize) => screenSize === ScreenSize.XSMALL),
    distinctUntilChanged(),
    tap((value) => (this._renderMenuAsBottomSheet = value)),
  );

  //   please avoid using this, use screenSize$ instead
  //   this is a legacy breakpoint that will be deprecated in the future
  //  currently used for video call component breakpoint and container indicators position
  legacyMobileBreakpoint$ = this.filterBreakpointsOperator(
    `(max-width: ${this._mobileMaxWidth})`,
  ).pipe(
    map((state) => state.matches),
    distinctUntilChanged(),
  );

  public get renderMenuAsBottomSheet() {
    return this._renderMenuAsBottomSheet;
  }

  private currentSpaceCanvasWidth = new BehaviorSubject<number>(window.innerWidth);
  public readonly currentSpaceCanvasWidth$ = this.currentSpaceCanvasWidth.asObservable().pipe(
    distinctUntilChanged(),
    auditTime(150),
    filter((width) => width > 0),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  public screenFitsBottomLeftToolbar$ = this.currentSpaceCanvasWidth$.pipe(
    map((spaceCanvasWidth) => spaceCanvasWidth > MIN_SPACE_CANVAS_WIDTH_NEEDED),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  public rightSectionBottomToolbarOverflowed$ = this.currentSpaceCanvasWidth$.pipe(
    map((width) => width < MIN_WIDTH_NEEDED_FOR_RIGHT_SECTION_BOTTOM_TOOLBAR_V2),
    distinctUntilChanged(),
    startWith(
      this.currentSpaceCanvasWidthValue < MIN_WIDTH_NEEDED_FOR_RIGHT_SECTION_BOTTOM_TOOLBAR_V2,
    ),
  );

  public leftSectionBottomToolbarOverflowed$ = this.currentSpaceCanvasWidth$.pipe(
    map((width) => width < MIN_WIDTH_NEEDED_FOR_LEFT_SECTION_BOTTOM_TOOLBAR_V2),
    distinctUntilChanged(),
    startWith(
      this.currentSpaceCanvasWidthValue < MIN_WIDTH_NEEDED_FOR_LEFT_SECTION_BOTTOM_TOOLBAR_V2,
    ),
  );

  /**
   * Observe one or multiple breakpoints, returning either the full BreakpointState or just the .matches boolean.
   *
   * @param breakpoints - The breakpoint or array of breakpoints to observe
   * @param returnType - 'boolean' to return Observable<boolean>, 'state' (default) to return Observable<BreakpointState>
   */
  public observeBreakpoint$<T extends boolean | BreakpointState>(
    breakpoints: string | string[],
    returnType: 'boolean' | 'state' = 'boolean',
  ): Observable<T> {
    const breakpointsArr = Array.isArray(breakpoints) ? breakpoints : [breakpoints];
    this.addBreakpointToObserver(...breakpointsArr);

    const breakpoint$ = this.filterBreakpointsOperator(...breakpointsArr);

    if (returnType === 'boolean') {
      return breakpoint$.pipe(
        map((state: BreakpointState) => state.matches),
        distinctUntilChanged(),
      ) as Observable<T>;
    }
    return breakpoint$ as Observable<T>;
  }

  /** Observe a single max-width media query (px), returning whether it currently matches the breakpoint. */
  public observeMaxWidth$(maxWidthPx: number): Observable<boolean> {
    return this.observeBreakpoint$<boolean>(`(max-width: ${maxWidthPx}px)`);
  }

  public firstBreakpointForScreenShareToolbarReached$ = this.filterBreakpointsOperator(
    `(max-width: ${FIRST_BREAKPOINT_FOR_SCREEN_SHARE_ANNOTATIONS_TOOLBAR}px)`,
  ).pipe(
    map((state) => state.matches),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public secondBreakpointForScreenShareToolbarReached$ = this.filterBreakpointsOperator(
    `(max-width: ${SECOND_BREAKPOINT_FOR_SCREEN_SHARE_ANNOTATIONS_TOOLBAR}px)`,
  ).pipe(
    map((state) => state.matches),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public get isMiddleSectionBottomToolbarOverflowed() {
    return (
      this.currentSpaceCanvasWidthValue < MIN_WIDTH_NEEDED_FOR_MIDDLE_SECTION_BOTTOM_TOOLBAR_V2
    );
  }

  private sidebarWidth = new BehaviorSubject<number>(0);

  private readonly resize$ = fromEvent(window, 'resize').pipe(
    startWith(null),
    auditTime(50),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  private readonly viewportWidth$ = this.resize$.pipe(
    map(() => window.innerWidth),
    distinctUntilChanged(),
  );

  /**
   * Width actually available to the session: the viewport minus the persistent Spaces Switcher
   * sidebar (0 when it's absent or collapsed). The panel bottom-sheet and bottom-toolbar breakpoints
   * are measured against this instead of the raw viewport, so they react to the sidebar the same way
   * the whiteboard does. Fed via {@link setSidebarWidth} (see AppComponent), mirroring how
   * currentSpaceCanvasWidth is pushed in.
   */
  readonly availableWidth$ = combineLatest([
    this.viewportWidth$,
    this.sidebarWidth.asObservable(),
  ]).pipe(
    map(([viewportWidth, sidebarWidth]) => Math.max(0, viewportWidth - sidebarWidth)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  get availableWidthValue(): number {
    return Math.max(0, window.innerWidth - this.sidebarWidth.getValue());
  }

  setSidebarWidth(width: number): void {
    this.sidebarWidth.next(width);
  }

  // Single comparison used by both the reactive breakpoints and their synchronous getters below,
  // so a breakpoint's threshold and "measured against available width" semantics live in one place.
  private availableWidthAtMost$(maxWidth: number): Observable<boolean> {
    return this.availableWidth$.pipe(
      map((width) => width <= maxWidth),
      distinctUntilChanged(),
    );
  }

  private isAvailableWidthAtMost(maxWidth: number): boolean {
    return this.availableWidthValue <= maxWidth;
  }

  readonly isUploadPanelBreakpointReached$ = this.availableWidthAtMost$(
    BOTTOM_SHEET_BREAKPOINT_WIDTH_UPLOAD_PANEL,
  );

  readonly isWidePanelBreakpointReached$ = this.availableWidthAtMost$(
    BOTTOM_SHEET_BREAKPOINT_WIDTH_FOR_WIDE_PANELS,
  );

  readonly isSystemStatusPanelBreakpointReached$ = this.availableWidthAtMost$(
    BOTTOM_SHEET_BREAKPOINT_WIDTH_SYSTEM_STATUS_PANEL,
  );

  readonly isPanelsBottomSheetBreakpointReached$ = this.availableWidthAtMost$(
    BOTTOM_SHEET_BREAKPOINT_WIDTH_PANEL,
  );

  // Shared "is this a desktop screen" observable (wider than the tablet breakpoint).
  readonly isDesktop$ = this.observeMaxWidth$(TABLET_MAX_WIDTH).pipe(
    map((isTabletOrSmaller) => !isTabletOrSmaller),
    distinctUntilChanged(),
  );

  setCurrentSpaceCanvasWidth(width: number) {
    // Ignore 0 (the canvas being torn down/hidden reports 0), so the raw getters stay consistent
    // with currentSpaceCanvasWidth$'s `filter(width > 0)` and don't briefly read a bogus width.
    if (width <= 0) {
      return;
    }
    this.currentSpaceCanvasWidth.next(width);
  }

  private addBreakpointToObserver(...breakpoints: string[]) {
    const breakpointsSet = this.breakpointsToObserver;
    breakpoints.forEach((breakpoint) => {
      if (!this.breakpointsToObserver.has(breakpoint)) {
        breakpointsSet.add(breakpoint);
      }
    });

    this.breakpointsToObserverSubject.next(breakpointsSet);
  }

  get breakpointsToObserver() {
    return this.breakpointsToObserverSubject.getValue();
  }

  get currentSpaceCanvasWidthValue() {
    return this.currentSpaceCanvasWidth.getValue();
  }

  get isPanelsBottomSheetBreakpointReached() {
    return this.isAvailableWidthAtMost(BOTTOM_SHEET_BREAKPOINT_WIDTH_PANEL);
  }

  get isUploadPanelBreakpointReached() {
    return this.isAvailableWidthAtMost(BOTTOM_SHEET_BREAKPOINT_WIDTH_UPLOAD_PANEL);
  }

  get isWidePanelBreakpointReached() {
    return this.isAvailableWidthAtMost(BOTTOM_SHEET_BREAKPOINT_WIDTH_FOR_WIDE_PANELS);
  }

  get isSystemStatusPanelBreakpointReached() {
    return this.isAvailableWidthAtMost(BOTTOM_SHEET_BREAKPOINT_WIDTH_SYSTEM_STATUS_PANEL);
  }
}
